import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import * as authService from '../../services/authService'
import {
  buildRewardAvailabilityDisplay,
  getRewardAvailability,
  notifyUserSummaryUpdated,
  POINTS_CONFIG_UPDATED_EVENT,
  redeemReward,
  type RewardAvailability,
} from '../../services/pointsService'
import {
  hasRewardDescriptionDetail,
  runRewardRedeemFlow,
  type RewardRedeemItem,
} from '@huay/shared/points/rewardRedeemFlow'
import type { PointsOutletContext } from './ModernPointsLayout'
import {
  createModernRewardRedeemHandlers,
  showModernRewardDetailModal,
} from './modernRewardRedeemHandlers'
import './pointsLayout.css'
import '@huay/shared/points/rewardsAnimations.css'

const formatNumber = (value: number) => Number(value || 0).toLocaleString('th-TH')

const PointsRewardsPage: React.FC = () => {
  const {
    getAssetUrl,
    pointsText,
    pointsBalance,
    pointsConfig,
    activeRewards,
    refreshUserSummary,
  } = useOutletContext<PointsOutletContext>()
  const [submittingRewardId, setSubmittingRewardId] = useState<string | null>(null)
  const [rewardAvailabilityMap, setRewardAvailabilityMap] = useState<Record<string, RewardAvailability>>({})
  const fallbackImage = getAssetUrl('/img/logo-m.png')
  const redeemHandlers = useMemo(() => createModernRewardRedeemHandlers(), [])

  const rewardsEnabled = pointsConfig?.system?.rewardsEnabled !== false
  const rewardList = useMemo(() => activeRewards, [activeRewards])
  const hasActiveRewards = rewardsEnabled && rewardList.length > 0
  const systemStatusText = hasActiveRewards
    ? 'พร้อมแลกรางวัล'
    : rewardsEnabled
      ? 'ยังไม่มีรางวัลที่เปิดใช้งาน'
      : 'ปิดระบบแลกรางวัล'
  const systemStatusTone = hasActiveRewards ? 'ready' : rewardsEnabled ? 'empty' : 'off'

  const loadRewardAvailability = useCallback(async () => {
    const currentUser = authService.getCurrentUser()
    const tenantId = currentUser?.tenantId
    const userId = currentUser?.id
    if (!tenantId || !userId || !hasActiveRewards) {
      setRewardAvailabilityMap({})
      return
    }

    try {
      const nextMap = await getRewardAvailability({ tenantId, userId })
      setRewardAvailabilityMap(nextMap)
    } catch (error) {
      console.error('Failed to load reward availability:', error)
    }
  }, [hasActiveRewards])

  useEffect(() => {
    void loadRewardAvailability()
  }, [loadRewardAvailability, rewardList])

  useEffect(() => {
    const handlePointsConfigUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ tenantId?: string }>).detail
      const currentUser = authService.getCurrentUser()
      if (detail?.tenantId && detail.tenantId !== currentUser?.tenantId) return
      void loadRewardAvailability()
    }
    window.addEventListener(POINTS_CONFIG_UPDATED_EVENT, handlePointsConfigUpdated)
    return () => window.removeEventListener(POINTS_CONFIG_UPDATED_EVENT, handlePointsConfigUpdated)
  }, [loadRewardAvailability])

  const handleRedeem = async (reward: RewardRedeemItem) => {
    const currentUser = authService.getCurrentUser()
    const tenantId = currentUser?.tenantId
    const userId = currentUser?.id

    if (!tenantId || !userId) {
      await redeemHandlers.onError('กรุณาเข้าสู่ระบบใหม่อีกครั้ง')
      return
    }

    const availability = rewardAvailabilityMap[reward.id]
    const availabilityDisplay = buildRewardAvailabilityDisplay(
      { type: reward.type, stock: -1, limitPerDay: 0, limitPerUserPerDay: 0 },
      availability
    )

    try {
      setSubmittingRewardId(reward.id)
      await runRewardRedeemFlow({
        reward,
        pointsBalance,
        canRedeem: availabilityDisplay.canRedeem,
        unavailableLabel: availabilityDisplay.unavailableLabel || undefined,
        handlers: redeemHandlers,
        redeem: async (shippingInfo) => {
          const response = await redeemReward({
            tenantId,
            userId,
            rewardId: reward.id,
            shippingInfo,
          })
          notifyUserSummaryUpdated()
          await refreshUserSummary()
          await loadRewardAvailability()
          return { message: response.message }
        },
      })
    } finally {
      setSubmittingRewardId(null)
    }
  }

  return (
    <>
      <div className="c_quinary text-center">
        <h3 className="c_tertiary font_md">แลกรางวัล</h3>
      </div>

      <div className="light_sd border_10 p-3 p-md-4 bg_op_secondary bg_blur mt-3 mb-3 rewards-panel huay-rewards-animate">
        <div className={`rewards-status-bar rewards-status-bar--${systemStatusTone} bg_gd_secondary border_5 bdc_primary ins_sd`}>
          <span className="rewards-status-bar__icon bg_gd_quaternary">
            <i className={`fas ${hasActiveRewards ? 'fa-check' : rewardsEnabled ? 'fa-gift' : 'fa-ban'} c_tertiary`} aria-hidden="true" />
          </span>
          <div className="rewards-status-bar__copy">
            <span className="rewards-status-bar__label c_quinary">ระบบแลกรางวัล</span>
            <span className={`rewards-status-bar__value rewards-status-bar__value--${systemStatusTone}`}>
              {systemStatusText}
            </span>
          </div>
        </div>

        <div className="rewards-points-hero">
          <p className="rewards-points-hero__label c_quinary text-center mb-2">คะแนนของคุณ</p>
          <div className="text-center">
            <span className="rewards-points-hero__badge">{pointsText}</span>
          </div>
        </div>

        {hasActiveRewards ? (
          <div className="rewards-grid">
            {rewardList.map((reward, index) => {
              const availability = rewardAvailabilityMap[reward.id]
              const availabilityDisplay = buildRewardAvailabilityDisplay(reward, availability)
              const redeemDisabled = submittingRewardId === reward.id || !availabilityDisplay.canRedeem
              const showDetail = hasRewardDescriptionDetail(reward.description)

              return (
                <article
                  key={reward.id}
                  className="rewards-card ins_sd bdc_primary"
                  style={{ '--reward-enter-delay': `${0.16 + Math.min(index, 10) * 0.05}s` } as React.CSSProperties}
                >
                  <div className="rewards-card__media bg_gd_primary border_5">
                    <img
                      src={reward.imageUrl || fallbackImage}
                      alt={reward.name}
                      className="rewards-card__image"
                      loading="lazy"
                    />
                    {availabilityDisplay.stockBadge ? (
                      <span className={`rewards-card__stock rewards-card__stock--${availabilityDisplay.stockBadgeTone}`}>
                        {availabilityDisplay.stockBadge}
                      </span>
                    ) : null}
                    <span className={`rewards-card__type rewards-card__type--${reward.type}`}>
                      {reward.type === 'credit' ? 'เครดิต' : 'ของรางวัล'}
                    </span>
                  </div>
                  <div className="rewards-card__body">
                    <h5 className="rewards-card__title c_tertiary font_md mb-2">{reward.name}</h5>
                    <div className="rewards-card__meta">
                      <span className="rewards-card__cost">
                        <i className="fas fa-star" aria-hidden="true" />
                        ใช้ {formatNumber(reward.pointsCost)} คะแนน
                      </span>
                      {reward.type === 'credit' && (
                        <span className="rewards-card__credit">
                          ได้เครดิต {formatNumber(reward.creditAmount)} บาท
                        </span>
                      )}
                    </div>
                    {availabilityDisplay.hintLines.length > 0 ? (
                      <div className="rewards-card__availability-hints">
                        {availabilityDisplay.hintLines.map((line) => (
                          <span key={`${reward.id}-${line}`} className="rewards-card__availability-hint">
                            {line}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {reward.description ? (
                      <div className="rewards-card__desc-wrap">
                        <p className="rewards-card__desc c_quinary font_sm mb-0">{reward.description}</p>
                        {showDetail ? (
                          <button
                            type="button"
                            className="rewards-card__detail-badge"
                            onClick={() =>
                              void showModernRewardDetailModal({
                                name: reward.name,
                                description: reward.description,
                                imageUrl: reward.imageUrl || fallbackImage,
                                rewardType: reward.type,
                                stock: reward.stock,
                                stockLabel: availabilityDisplay.stockBadge,
                                stockTone: availabilityDisplay.stockBadgeTone,
                              })
                            }
                          >
                            รายละเอียด
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="link_pmr box_row justify-content-center font_rg bg_gd_quaternary border_10 ins_sd w-100 border-0 rewards-card__action"
                      onClick={() => void handleRedeem(reward)}
                      disabled={redeemDisabled}
                    >
                      <span className="c_tertiary font_sm d-flex align-items-center gap-2 mb-0">
                        <i className="fas fa-gift" aria-hidden="true" />
                        {submittingRewardId === reward.id
                          ? 'กำลังแลก...'
                          : availabilityDisplay.unavailableLabel || 'แลกรางวัล'}
                      </span>
                    </button>
                  </div>
                </article>
              )
            })}
          </div>
        ) : (
          <div className="rewards-empty bg_gd_secondary border_5 bdc_primary ins_sd">
            <div className="rewards-empty__icon bg_gd_quaternary">
              <i className="fas fa-gift c_tertiary" aria-hidden="true" />
            </div>
            <h5 className="c_tertiary font_md mb-2">ยังไม่พร้อมใช้งาน</h5>
            <p className="c_quinary font_sm mb-4">
              {rewardsEnabled
                ? 'ยังไม่มีรางวัลที่เปิดให้แลกในขณะนี้ กรุณาติดตามอัปเดตจากทางเว็บ'
                : 'ระบบแลกรางวัลถูกปิดอยู่ชั่วคราว'}
            </p>
            <Link
              to="/points/reward-history"
              className="link_pmr box_row justify-content-center font_rg bg_op_secondary border_10 ins_sd text-decoration-none px-4 py-2"
            >
              <span className="c_primary font_sm d-flex align-items-center gap-2">
                <i className="fas fa-history" aria-hidden="true" />
                ดูประวัติแลกรางวัล
              </span>
            </Link>
          </div>
        )}
      </div>
    </>
  )
}

export default PointsRewardsPage
